'use client'

// Shared Attachments modal (std/site/gallery/forum): the full management
// surface — upload, progress, remove, restore. The editor body keeps only the
// inline thumbnail strip (AttachmentsStrip). Behavior rides the existing v2
// attachment facade actions; the modal owns no attachment state of its own.
// Avatar entries are excluded here — the avatar is managed in the Configure
// modal's Avatar section.

import type { ChangeEvent, MouseEvent, RefObject } from 'react'
import { useRef } from 'react'
import { classNames } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import type { EditorAttachmentState } from '@/msgdeskRuntime/state'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { useEditorSurface, type EditorAttachmentsUiSkin } from '../../EditorSurfaceContext'
import { useEditorAttachments, visibleEditorAttachments } from '../../useEditorAttachments'
import { EditorModal } from './EditorModal'

export function useEditorAttachmentsUiSkin(): EditorAttachmentsUiSkin {
  const { skin } = useEditorSurface()
  if (!skin.attachmentsUi) {
    throw new Error('The attachments modal/strip requires the surface EditorSkin to supply the optional `attachmentsUi` token group')
  }
  return skin.attachmentsUi
}

// BEHAVIOUR: toggle/remove an attachment through the v2 facade. The facade
// keeps legacy save data in sync; React owns the visible tile state.
function toggleAttachment(event: MouseEvent<HTMLElement>, attachment: EditorAttachmentState, isActive: boolean) {
  if (!attachment.id) return
  event.preventDefault()
  event.stopPropagation()
  dispatchEditorAction({ action: 'toggle-attachment', attachment, active: isActive })
}

function AttachmentTile({ attachment, ui }: { attachment: EditorAttachmentState; ui: EditorAttachmentsUiSkin }) {
  const isActive = attachment.active !== false
  const status = attachment.status === 'uploading'
    ? attachment.progress || 'Uploading...'
    : attachment.error || (isActive ? 'Attached' : 'Will be removed')

  return (
    <article
      className={classNames(
        'md-attachments__tile',
        ui.tile,
        !isActive && 'md-attachments__tile--inactive',
        !isActive && ui.tileInactive,
        attachment.status === 'error' && 'md-attachments__tile--error',
        attachment.status === 'error' && ui.tileError,
      )}
    >
      <img className={classNames('md-attachments__preview', ui.preview)} src={attachment.src} alt="" />
      <div className={classNames('md-attachments__name', ui.name)} title={attachment.filename || ''}>{attachment.filename || 'Attachment'}</div>
      <div className={classNames('md-attachments__status', ui.status)}>{status}</div>
      {attachment.removable ? (
        <button
          type="button"
          className={classNames('md-attachments__remove', ui.remove)}
          onClick={event => toggleAttachment(event, attachment, false)}
          title="Remove attachment"
          aria-label="Remove attachment"
        >
          ×
        </button>
      ) : null}
      {!isActive && attachment.toggleable ? (
        <button
          type="button"
          className={classNames('md-attachments__restore', ui.restore)}
          onClick={event => toggleAttachment(event, attachment, true)}
        >
          Restore
        </button>
      ) : null}
    </article>
  )
}

function UploadTile({ inputRef, ui }: { inputRef: RefObject<HTMLInputElement | null>; ui: EditorAttachmentsUiSkin }) {
  return (
    <button
      type="button"
      className={classNames('md-attachments__tile md-attachments__tile--upload', ui.uploadTile)}
      onClick={() => inputRef.current?.click()}
    >
      <span className={classNames('md-attachments__upload-glyph', ui.uploadPreview)} aria-hidden="true">+</span>
      <span className={classNames('md-attachments__upload-label', ui.uploadLabel)}>Upload</span>
    </button>
  )
}

function HiddenAttachmentInput({ inputRef }: { inputRef: RefObject<HTMLInputElement | null> }) {
  const { attachmentAccept, attachmentMultiple } = useEditorSurface()
  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files || [])
    event.currentTarget.value = ''
    for (const file of files) {
      await dispatchEditorAction({ action: 'upload-attachment', file })
    }
  }

  return (
    <input
      ref={inputRef}
      type="file"
      accept={attachmentAccept}
      multiple={attachmentMultiple}
      className="md-attachments__input"
      hidden
      onChange={handleChange}
    />
  )
}

export function editorAttachmentCount(attachments: EditorAttachmentState[], editorMode?: EditorMode): number {
  return visibleEditorAttachments(attachments, editorMode).filter(attachment => attachment.active !== false).length
}

export function EditorAttachmentsModal({
  open,
  onClose,
  editorMode,
}: {
  open: boolean
  onClose: () => void
  editorMode?: EditorMode
}) {
  const ui = useEditorAttachmentsUiSkin()
  const attachments = visibleEditorAttachments(useEditorAttachments(), editorMode)
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <EditorModal open={open} onClose={onClose} title="Attachments">
      <div className={classNames('md-attachments__grid', ui.grid)}>
        <UploadTile inputRef={inputRef} ui={ui} />
        {attachments.length ? (
          attachments.map((attachment, index) => (
            <AttachmentTile key={attachment.id || `attachment-${index}`} attachment={attachment} ui={ui} />
          ))
        ) : (
          <div className={classNames('md-attachments__empty', ui.empty)}>No attachments yet</div>
        )}
      </div>
      <HiddenAttachmentInput inputRef={inputRef} />
    </EditorModal>
  )
}
